// Each driver part's own FBX→trained-branch-frame transform, solved from the geometry.
//
// Training does NOT use one conversion for the whole rig. `build_v5_meshes.py:get_part`
// solves a SIGNED AXIS PERMUTATION plus a TRANSLATION per part — "FBX->geo conversion
// solved once at pose 0, then frozen" — and only then applies the shared Y flip:
//
//     branch_vertex = ((v_fbx * 0.01) @ Pm.T + shift) @ YFLIP.T
//
// Assume one global conversion instead and it comes out right for whichever part's solved
// permutation you happened to guess and wrong for the rest, which is what "the body
// renders but the head and clothes don't" looks like from the outside. The head's error is
// a ~5.5 cm pure translation; the clothes' is three different translations at once, so no
// single rigid fit can absorb it.
//
// We can solve it EXACTLY where training had to approximate. It matched parts to the
// export with a KD-tree because it had no correspondence; we do — `faces[corner]` names
// the trained vertex that corner belongs to. So per part the translation is closed-form
// (the mean residual) and the permutation is the best of 48, with nothing to converge.

// Centimetres to metres, exactly as training's `v * 0.01`. Solved for, never guessed:
// see PART_FIT_MAX_RESIDUAL_METRES.
const CENTIMETRES_TO_METRES = 0.01

/** Metres per rig unit — the fit's own cm→m, named so code OUTSIDE the fit can convert a
 *  world-space quantity into the frame the skeleton is authored in. A generated clip
 *  states world metres; a rig unit here is a centimetre, and only the fit knows that. */
export const RIG_UNITS_PER_METRE = 1 / CENTIMETRES_TO_METRES

// A fit worse than this is not a frame convention, it is the wrong part or the wrong
// vertex order — training's own pose0-vs-export agreement is ~1 mm and its rig-topology
// validation 1.1 mm mean / 3.15 mm worst, so centimetres here means something is wrong.
export const PART_FIT_MAX_RESIDUAL_METRES = 0.08

/** The 48 signed axis permutations: which source axis each target axis reads, and its sign. */
export function signedPermutations() {
  const orders = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]]
  const out = []
  for (const order of orders) {
    for (let signs = 0; signs < 8; signs++) {
      out.push({
        axes: order,
        signs: [signs & 1 ? -1 : 1, signs & 2 ? -1 : 1, signs & 4 ? -1 : 1],
      })
    }
  }
  return out
}

/** `label` for a permutation, e.g. `(-y, -z, +x)` — for the fit report. */
export function describePermutation({ axes, signs }) {
  const names = ['x', 'y', 'z']
  return `(${axes.map((axis, i) => `${signs[i] < 0 ? '-' : '+'}${names[axis]}`).join(', ')})`
}

/**
 * Solve one part's transform against the trained vertices its corners map onto.
 *
 * @param {Float32Array} corners  the part's own positions, (C,3), in its FBX frame
 * @param {Uint32Array}  faces    branch vertex index per corner, offset by `firstCorner`
 * @param {number} firstCorner    where this part's corners start in the branch's `faces`
 * @param {Float32Array} target   the branch's trained neutral vertices, (V,3)
 * @returns {{permutation, translation: Float32Array, residualMean, residualMax}}
 */
export function fitPartFrame(corners, faces, firstCorner, target) {
  const count = corners.length / 3
  let best = null
  for (const permutation of signedPermutations()) {
    const { axes, signs } = permutation
    // Closed-form translation: the mean of (target - permuted source) minimises the
    // squared residual, so each candidate costs one pass and no search.
    let sumX = 0, sumY = 0, sumZ = 0
    for (let index = 0; index < count; index++) {
      const source = index * 3
      const vertex = faces[firstCorner + index] * 3
      sumX += target[vertex] - signs[0] * corners[source + axes[0]] * CENTIMETRES_TO_METRES
      sumY += target[vertex + 1] - signs[1] * corners[source + axes[1]] * CENTIMETRES_TO_METRES
      sumZ += target[vertex + 2] - signs[2] * corners[source + axes[2]] * CENTIMETRES_TO_METRES
    }
    const translation = new Float32Array([sumX / count, sumY / count, sumZ / count])
    let sum = 0
    let worst = 0
    for (let index = 0; index < count; index++) {
      const source = index * 3
      const vertex = faces[firstCorner + index] * 3
      const distance = Math.hypot(
        signs[0] * corners[source + axes[0]] * CENTIMETRES_TO_METRES + translation[0] - target[vertex],
        signs[1] * corners[source + axes[1]] * CENTIMETRES_TO_METRES + translation[1] - target[vertex + 1],
        signs[2] * corners[source + axes[2]] * CENTIMETRES_TO_METRES + translation[2] - target[vertex + 2])
      sum += distance
      if (distance > worst) worst = distance
    }
    const residualMean = sum / count
    if (!best || residualMean < best.residualMean) {
      best = { permutation, translation, residualMean, residualMax: worst }
    }
  }
  return best
}

/** A solved permutation as a row-major 3x3 rotation: `out[i] = M[i][j] * src[j]`.
 *
 *  The fit's ROTATION alone — no cm→m scale, no translation — because that is what a
 *  bone ORIENTATION transforms by. Pure numbers, like the rest of this module. */
export function permutationMatrix({ axes, signs }) {
  const matrix = [0, 0, 0, 0, 0, 0, 0, 0, 0]
  for (let row = 0; row < 3; row++) matrix[row * 3 + axes[row]] = signs[row]
  return matrix
}

/** det of a row-major 3x3. A signed permutation can be a REFLECTION (det -1), which is
 *  not a rotation and cannot pose a skeleton — the caller has to know rather than
 *  silently produce a mirrored character. */
export function matrixDeterminant(m) {
  return m[0] * (m[4] * m[8] - m[5] * m[7])
    - m[1] * (m[3] * m[8] - m[5] * m[6])
    + m[2] * (m[3] * m[7] - m[4] * m[6])
}

/** The one permutation every part solved, or null when they disagree.
 *
 *  Parts are fitted INDEPENDENTLY and only their translations are expected to differ
 *  (measured on myrav3: same (-y, -z, +x) everywhere, translations 22 / 32 / 55 mm). They
 *  must agree on the rotation, because one skeleton poses all of them — a per-part
 *  rotation would mean the head and the body face different ways. Null rather than a
 *  first-one-wins guess: the caller reports it and leaves the clip uncorrected. */
export function sharedPermutation(fits) {
  const all = (fits ?? []).filter(Boolean)
  if (!all.length) return null
  const first = all[0].permutation
  const same = (other) => other.axes.every((axis, i) => axis === first.axes[i])
    && other.signs.every((sign, i) => sign === first.signs[i])
  return all.every((fit) => same(fit.permutation)) ? first : null
}

/** Apply a solved fit to one point, in place. */
export function applyPartFrame(fit, point) {
  const { axes, signs } = fit.permutation
  const source = [point.x, point.y, point.z]
  return point.set(
    signs[0] * source[axes[0]] * CENTIMETRES_TO_METRES + fit.translation[0],
    signs[1] * source[axes[1]] * CENTIMETRES_TO_METRES + fit.translation[1],
    signs[2] * source[axes[2]] * CENTIMETRES_TO_METRES + fit.translation[2])
}

/** `head/SKM_…FaceMesh: (-y, -z, +x) +[0.006, 0.055, 0.000]m  fit 1.6mm/18.2mm` */
export function formatPartFrame(branch, part, fit) {
  const millimetres = (metres) => `${(metres * 1000).toFixed(1)}mm`
  const offset = [...fit.translation].map((value) => value.toFixed(3)).join(', ')
  return `${branch}/${part}: ${describePermutation(fit.permutation)} +[${offset}]m  `
    + `fit ${millimetres(fit.residualMean)}/${millimetres(fit.residualMax)}`
}
